import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { gql } from "@apollo/client";
import { FileSpreadsheet, UploadCloud, X, ChevronLeft } from "lucide-react";
import { useDropzone } from "react-dropzone";
import { Button } from "@src/lib/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@src/lib/ui/Dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@src/lib/ui/AlertDialog";
import { useToast } from "@src/lib/hooks/useToast";
import {
  GraphqlOperations,
  GuestFeedbackBatchFileInput,
  GuestFeedbackUploadBatchStatus,
} from "@src/types.generated";
import { GuestFeedbackCsvMappingDialog } from "./GuestFeedbackUploadCSVDialog";
import { useGuestFeedbackCsvImport } from "./useGuestFeedbackCsvImport";
import {
  GuestFeedbackBatchDetailsFragment,
  PreviewGuestFeedbackUploadBatchMutation,
  useGuestFeedbackUploadAnalysisStatusQuery,
  useGuestFeedbackUploadBatchLazyQuery,
  usePreviewGuestFeedbackUploadBatchMutation,
  useSubmitGuestFeedbackUploadBatchMutation,
  useRetryGuestFeedbackUploadBatchMutation,
} from "./GuestFeedbackUploadBatchDialog.generated";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batch?: GuestFeedbackBatchDetailsFragment;
};
type Preview =
  PreviewGuestFeedbackUploadBatchMutation["previewGuestFeedbackUploadBatch"]["files"];
// A file in the draft. `key` identifies it while editing; saved files also
// carry the server `id` in `file`, which a retry sends to keep them.
type DraftFile = { key: string; file: GuestFeedbackBatchFileInput };

export const batchFiles = (
  batch: GuestFeedbackBatchDetailsFragment,
): DraftFile[] =>
  batch.files.map((file) => ({
    key: file.id,
    file: {
      id: file.id,
      fileName: file.fileName ?? "",
      csv: {
        fileUrl: file.fileUrl,
        feedbackTextColumn: file.feedbackTextColumn,
        feedbackDateColumn: file.feedbackDateColumn,
        ratingColumn: file.ratingColumn,
        ratingColumnMin: file.ratingColumnMin!,
        ratingColumnMax: file.ratingColumnMax!,
        locationColumn: file.locationColumn,
        feedbackSource: file.feedbackSource,
        locationMappings: file.locations.map((location) => ({
          locationName: location.locationName,
          locationId: location.location.id,
        })),
      },
    },
  }));

// Analysis only reads the last 90 days, so older reviews import but are never
// analyzed. Say so before the user submits.
export function oldReviewsWarning({
  reviewCount,
  oldReviewCount,
}: {
  reviewCount: number;
  oldReviewCount: number;
}): string {
  if (oldReviewCount >= reviewCount)
    return "None of these reviews are from the last 90 days, so they won't be analyzed.";
  return `${oldReviewCount.toLocaleString()} of ${reviewCount.toLocaleString()} reviews are older than 90 days and won't be analyzed.`;
}

const BatchDialog: React.FC<Props> = ({ onOpenChange, batch }) => {
  const [drafts, setDrafts] = useState<DraftFile[]>(() =>
    batch ? batchFiles(batch) : [],
  );
  const files = useMemo(() => drafts.map((draft) => draft.file), [drafts]);
  const [revision, setRevision] = useState(batch?.revision);
  const [selectedFile, setSelectedFile] = useState<File>();
  const [mapping, setMapping] = useState(!batch);
  const [editing, setEditing] = useState<DraftFile>();
  const [dirty, setDirty] = useState(false);
  const [discard, setDiscard] = useState(false);
  // Previews for the file list they were computed from. Rows are matched by
  // position, so previews for any other list are not shown.
  const [checked, setChecked] = useState<{
    files: GuestFeedbackBatchFileInput[];
    previews: Preview;
  }>();
  const previews = checked?.files === files ? checked.previews : undefined;
  // The batch's files as last saved, for their import errors.
  const [savedFiles, setSavedFiles] = useState(batch?.files);
  // Set when the batch was resolved elsewhere, so there is nothing to retry.
  const [resolvedElsewhere, setResolvedElsewhere] = useState(false);
  // Set when a retry was rejected and the batch could not be reloaded, so the
  // saved revision may be stale. Submitting waits for a successful refresh.
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [previewError, setPreviewError] = useState<string>();
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [submissionId, setSubmissionId] = useState(() => crypto.randomUUID());
  const [submissionError, setSubmissionError] = useState<string>();
  const generation = useRef(0);
  const [previewAttempt, setPreviewAttempt] = useState(0);
  const markDirty = useCallback(() => setDirty(true), []);
  const { addToast, addErrorToast } = useToast();
  const { notifyCsvUploaded } = useGuestFeedbackCsvImport();
  const { data: statusData } = useGuestFeedbackUploadAnalysisStatusQuery({
    pollInterval: 5000,
    fetchPolicy: "network-only",
  });
  const analysisRunning =
    !!statusData?.GuestFeedbackUploadAnalysisStatus.activeRun;
  const analysisQueued =
    (statusData?.GuestFeedbackUploadAnalysisStatus.waitingBatchCount ?? 0) > 0;
  const analysisStatusMessage = analysisRunning
    ? "An analysis is running now. These files will be included in the next batch."
    : analysisQueued
      ? `Analysis is queued. Reviews from ${!mapping && files.length > 1 ? "these files" : "this file"} will be included if they finish importing before analysis starts. Otherwise, they’ll be analyzed in the next run.`
      : undefined;
  const [preview] = usePreviewGuestFeedbackUploadBatchMutation();
  // The status list polls slowly when idle, so refresh it on submit.
  const refetchQueries = [GraphqlOperations.Query.GuestFeedbackUploadBatches];
  const [submit] = useSubmitGuestFeedbackUploadBatchMutation({
    refetchQueries,
  });
  const [retry] = useRetryGuestFeedbackUploadBatchMutation({ refetchQueries });
  const [loadBatch] = useGuestFeedbackUploadBatchLazyQuery({
    fetchPolicy: "network-only",
  });
  // Show a batch that changed since this dialog loaded it. Returns whether it
  // had changed.
  const applyChangedBatch = (current: GuestFeedbackBatchDetailsFragment) => {
    if (current.revision === revision) return false;
    setDrafts(batchFiles(current));
    setSavedFiles(current.files);
    setRevision(current.revision);
    setResolvedElsewhere(
      current.status !== GuestFeedbackUploadBatchStatus.NeedsAttention,
    );
    setSubmissionError(
      current.status === GuestFeedbackUploadBatchStatus.NeedsAttention
        ? "This batch was changed. Review its current files and try again."
        : "This batch was changed and no longer needs attention. Close this dialog to see it.",
    );
    return true;
  };
  const refreshBatch = async () => {
    if (!batch) return;
    setRefreshing(true);
    const current = await loadCurrentBatch(batch.id);
    setRefreshing(false);
    if (!current) return;
    setRefreshFailed(false);
    if (!applyChangedBatch(current)) setSubmissionError(undefined);
  };
  // The batch as saved now, or undefined if it cannot be loaded.
  const loadCurrentBatch = async (id: string) => {
    try {
      return (await loadBatch({ variables: { id } })).data
        ?.GuestFeedbackUploadBatch;
    } catch {
      return undefined;
    }
  };

  useEffect(() => {
    const current = ++generation.current;
    if (!files.length) {
      setChecked(undefined);
      setPreviewError(undefined);
      setPreviewing(false);
      return;
    }
    setPreviewing(true);
    setPreviewError(undefined);
    preview({ variables: { files } })
      .then((result) => {
        if (current !== generation.current) return;
        if (!result.data?.previewGuestFeedbackUploadBatch.success)
          throw new Error(
            result.data?.previewGuestFeedbackUploadBatch.error?.message ??
              "Could not check these files.",
          );
        setChecked({
          files,
          previews: result.data.previewGuestFeedbackUploadBatch.files,
        });
      })
      .catch((error) => {
        if (current === generation.current)
          setPreviewError(error.message ?? "Could not check these files.");
      })
      .finally(() => {
        if (current === generation.current) setPreviewing(false);
      });
    return () => {
      generation.current = current + 1;
    };
  }, [files, preview, previewAttempt]);

  const close = () => {
    if (submitting) return;
    if (resolvedElsewhere) onOpenChange(false);
    else if (dirty || attempted || (!batch && files.length)) setDiscard(true);
    else onOpenChange(false);
  };
  const onPrepared = (file: GuestFeedbackBatchFileInput) => {
    setDrafts((previous) =>
      editing
        ? previous.map((d) => (d.key === editing.key ? { ...d, file } : d))
        : [...previous, { key: crypto.randomUUID(), file }],
    );
    setDirty(true);
    setMapping(false);
    setEditing(undefined);
    setSelectedFile(undefined);
  };
  const {
    getRootProps,
    getInputProps,
    open: browseFiles,
  } = useDropzone({
    multiple: false,
    noClick: true,
    noKeyboard: true,
    accept: { "text/csv": [".csv"] },
    disabled: attempted || resolvedElsewhere,
    onDrop: (accepted) => {
      if (accepted[0]) {
        setEditing(undefined);
        setSelectedFile(accepted[0]);
        setMapping(true);
      }
    },
    onDropRejected: () =>
      addErrorToast({
        message: "Choose one CSV file at a time.",
        callsite: "guest_feedback_batch_file",
      }),
  });
  const submitFiles = async () => {
    setSubmitting(true);
    setAttempted(true);
    setSubmissionError(undefined);
    try {
      const result = batch
        ? (
            await retry({
              variables: {
                batchId: batch.id,
                expectedRevision: revision!,
                files,
              },
            })
          ).data?.retryGuestFeedbackUploadBatch
        : (await submit({ variables: { submissionId, files } })).data
            ?.submitGuestFeedbackUploadBatch;
      if (!result)
        throw new Error(
          "Could not confirm your submission. Retry to check the same submission.",
        );
      if (!result.success) {
        // A lost response to this dialog's own retry is replayed as a success,
        // so a rejection with a new revision means the batch changed elsewhere.
        const current = batch && (await loadCurrentBatch(batch.id));
        // A definitive rejection creates nothing, so the files can be edited.
        // Edited files are a new submission, so they get a new idempotency key.
        setAttempted(false);
        setSubmissionId(crypto.randomUUID());
        if (current && applyChangedBatch(current)) return;
        const message =
          result.error?.message ?? "Could not submit these files.";
        if (batch && !current) {
          setRefreshFailed(true);
          setSubmissionError(
            `${message} We could not check whether this batch changed. Refresh it before trying again.`,
          );
          return;
        }
        setSubmissionError(message);
        return;
      }
      addToast({
        message: !files.length
          ? "Batch cancelled"
          : result.batch?.status ===
              GuestFeedbackUploadBatchStatus.NeedsAttention
            ? "Some files need attention. Review them in CSV uploads."
            : result.batch?.status ===
                GuestFeedbackUploadBatchStatus.NoNewFeedback
              ? "No new reviews were added."
              : result.batch?.status ===
                  GuestFeedbackUploadBatchStatus.Analyzing
                ? "Your CSVs are being analyzed."
                : result.batch?.status ===
                    GuestFeedbackUploadBatchStatus.Completed
                  ? "Analysis complete."
                  : result.analysisRunning
                    ? "Your CSVs were submitted for the next analysis."
                    : "Your CSVs were submitted. Analysis will start after processing.",
      });
      if (files.length) notifyCsvUploaded();
      onOpenChange(false);
    } catch (error) {
      setSubmissionError(
        error instanceof Error
          ? error.message
          : "Could not confirm your submission. Retry to check the same submission.",
      );
    } finally {
      setSubmitting(false);
    }
  };
  const ready =
    !previewing &&
    !previewError &&
    (!files.length ||
      (!!previews &&
        previews.length === files.length &&
        previews.every((f) => !f.error)));

  return (
    <>
      {mapping ? (
        <GuestFeedbackCsvMappingDialog
          key={editing?.key ?? "new"}
          open
          onOpenChange={(value) => {
            if (!value) close();
          }}
          onPrepared={onPrepared}
          onCancel={
            files.length
              ? () => {
                  setMapping(false);
                  setEditing(undefined);
                  setSelectedFile(undefined);
                }
              : undefined
          }
          onDirty={markDirty}
          initialFile={editing?.file}
          selectedFile={selectedFile}
          analysisStatusMessage={analysisStatusMessage}
        />
      ) : (
        <Dialog
          open
          onOpenChange={(value) => {
            if (!value) close();
          }}
        >
          <DialogContent className="max-w-2xl" hideDefaultTitle>
            <DialogHeader>
              <DialogTitle>
                {files.length} {files.length === 1 ? "CSV" : "CSVs"} ready for
                analysis
              </DialogTitle>
              <DialogDescription>
                Opus will analyze your reviews for recent recurring themes and
                then surface any action items in Recommendations.
              </DialogDescription>
            </DialogHeader>
            <div className="flex max-h-[50vh] flex-col gap-2 overflow-y-auto">
              {drafts.map((draft, index) => {
                const { file } = draft;
                // Previews come back in the order the files were sent.
                const summary = previews?.[index];
                const existingError = savedFiles?.find(
                  (f) => f.id === file.id,
                )?.error;
                const error =
                  summary?.error ?? (!summary ? existingError : undefined);
                return (
                  <div
                    key={draft.key}
                    className="flex items-center gap-4 rounded-xl bg-bg-secondary p-4"
                  >
                    <FileSpreadsheet className="size-6 shrink-0 text-icon-secondary" />
                    <div className="min-w-0 flex-1">
                      <button
                        className="max-w-full truncate text-left text-sm font-medium text-text-primary underline-offset-2 hover:underline"
                        disabled={attempted || resolvedElsewhere}
                        onClick={() => {
                          markDirty();
                          setEditing(draft);
                          setMapping(true);
                        }}
                      >
                        {file.fileName}
                      </button>
                      {error ? (
                        <p
                          role="alert"
                          className="text-sm text-status-error-text"
                        >
                          {error}
                        </p>
                      ) : (
                        <p className="text-sm text-text-secondary">
                          {previewing
                            ? "Checking reviews…"
                            : summary
                              ? `${summary.reviewCount.toLocaleString()} review${summary.reviewCount === 1 ? "" : "s"} · ${summary.locationCount} location${summary.locationCount === 1 ? "" : "s"}${summary.earliestReviewDate && summary.latestReviewDate ? ` · ${new Date(summary.earliestReviewDate).toLocaleDateString()} – ${new Date(summary.latestReviewDate).toLocaleDateString()}` : ""}`
                              : ""}
                        </p>
                      )}
                      {!!summary &&
                        !!(
                          summary.duplicateRowCount || summary.invalidRowCount
                        ) && (
                          <p className="text-xs text-text-secondary">
                            {summary.duplicateRowCount} duplicate
                            {summary.duplicateRowCount === 1
                              ? ""
                              : "s"} and {summary.invalidRowCount} invalid{" "}
                            {summary.invalidRowCount === 1 ? "row" : "rows"}{" "}
                            will be skipped.
                          </p>
                        )}
                      {!!summary && summary.oldReviewCount > 0 && (
                        <p className="text-xs text-status-warning-text">
                          {oldReviewsWarning(summary)}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Remove ${file.fileName}`}
                      disabled={attempted || resolvedElsewhere}
                      onClick={() => {
                        markDirty();
                        setDrafts((previous) =>
                          previous.filter((d) => d.key !== draft.key),
                        );
                      }}
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                );
              })}
              {files.length < 20 && (
                <div
                  {...getRootProps()}
                  className="flex items-center gap-3 rounded-xl border border-dashed border-border-primary p-4 text-sm text-text-secondary"
                >
                  <input {...getInputProps()} />
                  <UploadCloud className="size-5" />
                  Drag and drop another CSV here
                  <Button
                    variant="outline"
                    className="ml-auto"
                    disabled={attempted || resolvedElsewhere}
                    onClick={browseFiles}
                  >
                    Browse files
                  </Button>
                </div>
              )}
            </div>
            {previewError && (
              <div role="alert" className="text-sm text-status-error-text">
                <p>{previewError}</p>
                <Button
                  variant="outline"
                  onClick={() => setPreviewAttempt((value) => value + 1)}
                >
                  Retry file check
                </Button>
              </div>
            )}
            {submissionError && (
              <div role="alert" className="text-sm text-status-error-text">
                <p>{submissionError}</p>
                {refreshFailed && (
                  <Button
                    variant="outline"
                    loading={refreshing}
                    onClick={refreshBatch}
                  >
                    Refresh batch
                  </Button>
                )}
              </div>
            )}
            <DialogFooter className="items-start sm:justify-between">
              <Button
                variant="outline"
                disabled={attempted || resolvedElsewhere || !files.length}
                onClick={() => {
                  markDirty();
                  setEditing(drafts[drafts.length - 1]);
                  setMapping(true);
                }}
              >
                <ChevronLeft className="size-4" />
                Back
              </Button>
              <div className="flex flex-col items-end gap-2">
                <Button
                  onClick={submitFiles}
                  loading={submitting}
                  disabled={
                    submitting ||
                    resolvedElsewhere ||
                    refreshFailed ||
                    (!attempted && (!ready || (!files.length && !batch)))
                  }
                >
                  {attempted
                    ? "Retry submission"
                    : !files.length && batch
                      ? "Cancel batch"
                      : analysisRunning || analysisQueued
                        ? "Add to queue"
                        : "Start analysis"}
                </Button>
                <p
                  role="status"
                  className="max-w-md text-right text-xs text-text-secondary"
                >
                  {analysisStatusMessage ??
                    "Recommendations typically ready in 15–30 minutes"}
                </p>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      <AlertDialog open={discard} onOpenChange={setDiscard}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {attempted ? "Close upload dialog?" : "Discard prepared files?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {attempted
                ? "Your submission may already be processing. Closing this dialog does not cancel submitted files."
                : batch
                  ? "Your unsaved changes will be discarded. The submitted batch will still need attention."
                  : "These files and mappings will be removed from this draft. No analysis has started."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={() => onOpenChange(false)}>
              {attempted ? "Close" : "Discard changes"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default function GuestFeedbackUploadDialog(
  props: Props,
): React.ReactElement | null {
  if (!props.open) return null;
  return <BatchDialog {...props} />;
}

gql`
  fragment GuestFeedbackBatchDetails on GuestFeedbackUploadBatch {
    id
    revision
    status
    createdAt
    files {
      id
      fileName
      status
      error
      importedReviewCount
      fileUrl
      feedbackTextColumn
      feedbackDateColumn
      ratingColumn
      ratingColumnMin
      ratingColumnMax
      locationColumn
      feedbackSource
      locations {
        id
        locationName
        location {
          id
        }
      }
    }
    analysisRun {
      id
      status
      startedAt
      completedAt
      error
    }
  }
  query GuestFeedbackUploadBatch($id: UUID!) {
    GuestFeedbackUploadBatch(id: $id) {
      ...GuestFeedbackBatchDetails
    }
  }
  query GuestFeedbackUploadAnalysisStatus {
    GuestFeedbackUploadAnalysisStatus {
      activeRun {
        id
        status
        startedAt
      }
      waitingBatchCount
    }
  }
  mutation PreviewGuestFeedbackUploadBatch(
    $files: [GuestFeedbackBatchFileInput!]!
  ) {
    previewGuestFeedbackUploadBatch(files: $files) {
      success
      error {
        code
        message
      }
      files {
        fileName
        reviewCount
        duplicateRowCount
        invalidRowCount
        locationCount
        earliestReviewDate
        latestReviewDate
        oldReviewCount
        error
      }
    }
  }
  mutation SubmitGuestFeedbackUploadBatch(
    $submissionId: UUID!
    $files: [GuestFeedbackBatchFileInput!]!
  ) {
    submitGuestFeedbackUploadBatch(submissionId: $submissionId, files: $files) {
      success
      error {
        code
        message
      }
      analysisRunning
      batch {
        ...GuestFeedbackBatchDetails
      }
    }
  }
  mutation RetryGuestFeedbackUploadBatch(
    $batchId: UUID!
    $expectedRevision: Int!
    $files: [GuestFeedbackBatchFileInput!]!
  ) {
    retryGuestFeedbackUploadBatch(
      batchId: $batchId
      expectedRevision: $expectedRevision
      files: $files
    ) {
      success
      error {
        code
        message
      }
      analysisRunning
      batch {
        ...GuestFeedbackBatchDetails
      }
    }
  }
`;
